<template>
  <div class="stock-table-section">
    <!-- Toolbar -->
    <div class="stock-table-toolbar">
      <div class="stock-table-toolbar-left">
        <span class="stock-table-title">
          Inventory Table
          <span class="stock-table-count"> {{ products.length }} products</span>
        </span>
      </div>
      <div class="stock-table-toolbar-right">
        <form autocomplete="off" class="stock-search-wrapper" @submit.prevent>
          <i class="search icon"></i>
          <input
            type="search"
            class="stock-search-input"
            placeholder="Search products..."
            :value="searchQuery"
            v-prevent-autofill="searchQuery"
            @input="$emit('search', $event.target.value)"
            @keyup.enter="$emit('search', $event.target.value)"
          />
        </form>
      </div>
    </div>

    <!-- Table -->
    <div style="overflow-x: auto;">
      <table class="stock-inventory-table">
        <colgroup>
          <col class="stock-col-product">
          <col class="stock-col-fba">
          <col class="stock-col-fbm">
          <col class="stock-col-velocity">
          <col class="stock-col-runway">
          <col class="stock-col-status">
        </colgroup>
        <thead>
          <tr>
            <th>Product</th>
            <th v-for="col in sortableColumns" :key="col.key" :class="['text-' + col.align, 'th-sort']">
              <span
                class="sort-col-head"
                :class="headClass(col.key)"
                role="button"
                tabindex="0"
                :title="'Sort by ' + col.label"
                @click.stop="$emit('sort', col.key)"
                @keydown.enter.prevent="$emit('sort', col.key)"
              >
                {{ col.label }}
                <span class="sort-col-icons" aria-hidden="true">
                  <svg v-if="sortKey === col.key && sortDir === 'asc'" class="sort-icon-filled" viewBox="0 0 24 24"><path fill="currentColor" d="M12 8l7 9H5z"/></svg>
                  <svg v-else-if="sortKey === col.key && sortDir === 'desc'" class="sort-icon-filled" viewBox="0 0 24 24"><path fill="currentColor" d="M12 16l-7-9h14z"/></svg>
                  <svg v-else class="sort-icon-neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 15l5 5 5-5M7 9l5-5 5 5"/></svg>
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in products" :key="product.productId">
            <!-- Product -->
            <td>
              <div class="stock-product-cell">
                <img
                  :src="product.imageUrl || product.largeImageUrl"
                  class="stock-product-img"
                  @click="$emit('show-details', product.productId)"
                />
                <div class="stock-product-info">
                  <span class="stock-product-title" :title="product.productTitle">{{ product.productTitle }}</span>
                  <div class="stock-product-meta">
                    <span class="stock-product-asin">{{ product.asin || String(product.productId || '') }}</span>
                    <span class="stock-product-separator">|</span>
                    <i :class="product.marketplace.toLowerCase().replace('uk', 'gb') + ' flag'" style="margin:0;"></i>
                    <span>{{ product.marketplace }}</span>
                  </div>
                </div>
              </div>
            </td>

            <!-- FBA Stock -->
            <td class="text-right">
              <div class="stock-qty-cell">
                <div :class="['stock-qty-main', { 'stock-qty-transferring': product._isTransferring }]">
                  {{ product._fbaTotal }}
                </div>
                <template v-if="product.fbaStockDetails">
                  <div class="stock-qty-detail">{{ product.fbaStockDetails }}</div>
                </template>
                <template v-else-if="product._fulfillable === 0">
                  <div class="stock-qty-detail">Fulfillable: {{ product._fulfillable }}</div>
                  <div class="stock-transfer-pill" v-if="product._reserved > 0">
                    <i class="exchange icon"></i>
                    {{ product._reserved }} transferring between FCs
                  </div>
                  <div class="stock-qty-detail" v-if="product.fbaInbound > 0">
                    Inbound: {{ fmtNum(product.fbaInbound) }}
                  </div>
                </template>
                <template v-else>
                  <div class="stock-qty-detail">Fulfillable: {{ product._fulfillable }}</div>
                  <div class="stock-qty-detail">Transferring: {{ product._reserved }}</div>
                  <div class="stock-qty-detail" v-if="product.fbaInbound > 0">
                    Inbound: {{ fmtNum(product.fbaInbound) }}
                  </div>
                </template>
              </div>
            </td>

            <!-- FBM Stock -->
            <td class="text-right">
              <div class="stock-qty-cell">
                <template v-if="product.fbmStock != null && product.fbmStock > 0">
                  <div class="stock-qty-main">{{ product.fbmStock }}</div>
                  <div class="stock-qty-detail">Own warehouse</div>
                </template>
                <div v-else class="stock-qty-main stock-qty-na">—</div>
              </div>
            </td>

            <!-- Velocity -->
            <td class="text-right">
              <div class="stock-velocity-cell">
                <template v-if="product.velocity !== null && product.velocity !== undefined">
                  <div class="stock-velocity-value">{{ product.velocity }}/day</div>
                  <div :class="['stock-velocity-trend', product._trendCls]">{{ product._trendText }}</div>
                </template>
                <div v-else class="stock-velocity-value stock-velocity-placeholder">—</div>
              </div>
            </td>

            <!-- Stock Runway -->
            <td class="text-right">
              <div class="stock-days-cell">
                <template v-if="product.daysLeft !== null && product.daysLeft !== undefined">
                  <div :class="['stock-days-value', product.healthStatus || '']">
                    {{ product.daysLeftDisplay || (product.daysLeft + ' days') }}
                  </div>
                  <div class="stock-days-detail" v-if="product.daysLeftSubtitle">{{ product.daysLeftSubtitle }}</div>
                  <div v-if="product.fbaInbound > 0" class="stock-inbound-badge-wrapper">
                    <div class="stock-inbound-badge">
                      <i class="info circle icon"></i>
                      +{{ product.fbaInbound }} inbound not counted
                    </div>
                  </div>
                </template>
                <div v-else class="stock-days-value stock-days-placeholder">—</div>
              </div>
            </td>

            <!-- Status -->
            <td class="text-center">
              <span :class="['stock-status-badge', product.healthStatus || 'unknown']">
                {{ product.status || 'Unknown' }}
              </span>
            </td>
          </tr>

          <tr v-if="products.length === 0">
            <td colspan="6">
              <div class="stock-empty-state">
                <i class="warehouse icon"></i>
                <p v-if="searchQuery">No products match your search.</p>
                <p v-else>No inventory data available yet.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Legend -->
    <div class="stock-table-legend" v-if="products.length > 0">
      <div class="stock-legend-header">
        <div class="stock-legend-title">Status logic:</div>
        <div class="stock-legend-items">
          <div class="stock-legend-item">
            <span class="stock-legend-dot critical"></span>
            <span><strong>Out of Stock</strong> — Fulfillable + Transferring = 0, no inbound</span>
          </div>
          <div class="stock-legend-item">
            <span class="stock-legend-dot low"></span>
            <span><strong>Low Stock</strong> — Effective runway ≤ 14 days</span>
          </div>
          <div class="stock-legend-item">
            <span class="stock-legend-dot healthy"></span>
            <span><strong>Healthy</strong> — Effective runway > 14 days</span>
          </div>
        </div>
      </div>
      <div class="stock-legend-note">
        <i class="info circle icon"></i>
        <span>Effective Stock = Fulfillable + Transferring (reservedStock). Inbound (not yet at FC) shown as info — excluded from runway.</span>
      </div>
    </div>

    <!-- Pagination -->
    <div class="stock-pagination" v-if="totalPages > 1">
      <button
        class="stock-pagination-btn"
        :class="{ disabled: currentPage === 0 }"
        @click="$emit('page', currentPage - 1)"
      >
        <i class="left chevron icon" style="margin:0;"></i>
      </button>
      <button
        v-for="page in paginationPages"
        :key="page"
        :class="['stock-pagination-btn', { active: currentPage === page }]"
        @click="$emit('page', page)"
      >
        {{ page + 1 }}
      </button>
      <button
        class="stock-pagination-btn"
        :class="{ disabled: currentPage >= totalPages - 1 }"
        @click="$emit('page', currentPage + 1)"
      >
        <i class="right chevron icon" style="margin:0;"></i>
      </button>
      <span class="stock-pagination-info">
        Page {{ currentPage + 1 }} of {{ totalPages }} ({{ totalItems }} items)
      </span>
    </div>
  </div>
</template>

<script>
var SORT_COLUMNS = [
  { key: 'fbaStock', label: 'FBA Stock', align: 'right' },
  { key: 'fbmStock', label: 'FBM Stock', align: 'right' },
  { key: 'velocity', label: 'Velocity', align: 'right' },
  { key: 'daysLeft', label: 'Stock Runway', align: 'right' },
  { key: 'status', label: 'Status', align: 'center' }
]

export default {
  name: 'StockInventoryTable',
  props: {
    products: { type: Array, default: function () { return [] } },
    sortKey: { type: String, default: 'fbaStock' },
    sortDir: { type: String, default: 'desc' },
    searchQuery: { type: String, default: '' },
    currentPage: { type: Number, default: 0 },
    totalPages: { type: Number, default: 0 },
    totalItems: { type: Number, default: 0 },
    paginationPages: { type: Array, default: function () { return [] } }
  },
  computed: {
    sortableColumns: function () { return SORT_COLUMNS }
  },
  methods: {
    headClass: function (col) {
      return {
        'sort-col-active': this.sortKey === col,
        'sort-col-asc': this.sortKey === col && this.sortDir === 'asc',
        'sort-col-desc': this.sortKey === col && this.sortDir === 'desc'
      }
    },
    fmtNum: function (n) {
      if (n == null) return '0'
      var num = Number(n)
      return num >= 1000 ? num.toLocaleString() : String(num)
    }
  }
}
</script>



// WEBPACK FOOTER //
// StockInventoryTable.vue?10b76db1